/**
 * Protected App Layout Route
 *
 * Auth guard for all authenticated pages. Uses beforeLoad
 * to check authentication BEFORE rendering (prevents flash of content).
 *
 * Sidebar rendering is delegated to child layout routes:
 * - _main/route.tsx: Main app sidebar (Dashboard, Projects, Inbox)
 * - settings/route.tsx: Settings sidebar
 * - admin/route.tsx: Admin sidebar
 */

import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { LayoutSkeleton } from '@/components/skeletons';
import { useAuthContext } from '@/providers/auth-provider';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    // Wait for auth to initialize
    if (context.auth.isLoading) {
      return;
    }

    // Redirect to login if not authenticated
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }

    // Pass user to child routes
    return { user: context.auth.user };
  },
  component: AppLayoutComponent,
});

function AppLayoutComponent() {
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return <LayoutSkeleton />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return <Outlet />;
}
